import * as THREE from 'three'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import vehicleJson from './vehicle/toyota-gt86.json'
import {
  createKunosLiteState,
  resetKunosLiteState,
  stepKunosLite,
  type KunosLiteState,
  type KunosLiteVehicle,
} from './vehicle/kunos-lite.ts'
import {
  createOctagonalRideState,
  resetOctagonalRideState,
  stepOctagonalRide,
  type OctagonalRideState,
  type OctagonalWheelSpec,
} from './vehicle/octagonal-wheel-physics.ts'
import {
  buildVehicleConvexHull,
  lowestVehicleHullVertices,
  type VehicleConvexHull,
} from './vehicle/vehicle-convex-hull.ts'
import {
  freezeVehicleTelemetryFrame,
  VEHICLE_TELEMETRY_SCHEMA_VERSION,
  type VehicleTelemetryFrameV1,
  type VehicleTelemetryWheelPosition,
} from './vehicle/telemetry.ts'

export type DriveInput = {
  throttle: number
  brake: number
  steering: number
  handbrake: boolean
}

export type SurfaceContact = {
  height: number
  grip: number
  road: boolean
  name?: string
}

export type SurfaceSampler = (x: number, z: number, referenceY?: number) => SurfaceContact

export type MotionResolver = (
  startX: number,
  startZ: number,
  endX: number,
  endZ: number,
  minY: number,
  maxY: number,
  radius: number,
) => { x: number; z: number; collided: boolean }

type WheelKey = 'frontLeft' | 'frontRight' | 'rearLeft' | 'rearRight'

export type VehicleRecord = KunosLiteVehicle & {
  id: string
  name: string
  physicsModel: string
  asset: { model: string; modelYawCorrectionDegrees: number }
  geometry: KunosLiteVehicle['geometry'] & {
    wheelWidthM: number
    wheels: Record<WheelKey, [number, number, number]>
  }
  presentation: { toonBands: number; outlineThicknessM: number }
}

type WheelVisual = {
  steering: THREE.Group
  rolling: THREE.Group
  front: boolean
  baseY: number
}

type RecoveryPose = { x: number; y: number; z: number; heading: number }

const wheelKeys: readonly WheelKey[] = ['frontLeft', 'frontRight', 'rearLeft', 'rearRight']
const telemetryWheelPositions: readonly VehicleTelemetryWheelPosition[] = [
  'front-left', 'front-right', 'rear-left', 'rear-right',
]
const worldUp = new THREE.Vector3(0, 1, 0)
const localX = new THREE.Vector3(1, 0, 0)
const localZ = new THREE.Vector3(0, 0, 1)
const clamp = THREE.MathUtils.clamp

export const TOYOTA_GT86 = vehicleJson as unknown as VehicleRecord

function toonGradient(bands: number) {
  const count = Math.max(2, Math.round(bands))
  const values = new Uint8Array(count)
  for (let index = 0; index < count; index += 1) values[index] = Math.round(48 + index / (count - 1) * 207)
  const texture = new THREE.DataTexture(values, count, 1, THREE.RedFormat)
  texture.minFilter = THREE.NearestFilter
  texture.magFilter = THREE.NearestFilter
  texture.generateMipmaps = false
  texture.needsUpdate = true
  return texture
}

function addOutline(mesh: THREE.Mesh, thickness: number) {
  const material = new THREE.ShaderMaterial({
    name: 'DC_TOON_OUTLINE',
    uniforms: { thickness: { value: thickness } },
    vertexShader: `
      uniform float thickness;
      void main() {
        vec3 displaced = position + normal * thickness;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(displaced, 1.0);
      }
    `,
    fragmentShader: 'void main() { gl_FragColor = vec4(0.025, 0.032, 0.033, 1.0); }',
    side: THREE.BackSide,
    depthWrite: true,
    toneMapped: false,
  })
  const outline = new THREE.Mesh(mesh.geometry, material)
  outline.name = `${mesh.name}_OUTLINE`
  outline.renderOrder = -1
  mesh.add(outline)
}

function toonMaterial(source: THREE.Material, gradient: THREE.Texture) {
  const original = source as THREE.Material & { color?: THREE.Color; emissive?: THREE.Color }
  const name = source.name.toLowerCase()
  const color = original.color?.clone() ?? new THREE.Color('#d6d7d2')
  if (name.includes('headlight')) color.set('#e8f2e9')
  if (name.includes('tail') || name.includes('brake')) color.set('#e74632')
  if (name.includes('glass')) color.set('#101b1e')
  const emissive = original.emissive?.clone() ?? new THREE.Color('#000000')
  if (name.includes('headlight')) emissive.set('#6f786f')
  if (name.includes('tail') || name.includes('brake')) emissive.set('#4e0804')
  return new THREE.MeshToonMaterial({
    name: source.name,
    color,
    emissive,
    emissiveIntensity: 0.45,
    gradientMap: gradient,
  })
}

function fallbackHull(record: VehicleRecord) {
  const halfWidth = record.geometry.widthM * 0.5
  const halfLength = record.geometry.lengthM * 0.5
  const bottom = record.geometry.groundClearanceM
  const top = record.geometry.heightM
  return buildVehicleConvexHull([
    new THREE.Vector3(-halfWidth, bottom, -halfLength),
    new THREE.Vector3(halfWidth, bottom, -halfLength),
    new THREE.Vector3(-halfWidth, bottom, halfLength),
    new THREE.Vector3(halfWidth, bottom, halfLength),
    new THREE.Vector3(-halfWidth * 0.72, top, -halfLength * 0.35),
    new THREE.Vector3(halfWidth * 0.72, top, -halfLength * 0.35),
    new THREE.Vector3(-halfWidth * 0.72, top, halfLength * 0.35),
    new THREE.Vector3(halfWidth * 0.72, top, halfLength * 0.35),
  ], { skinM: record.presentation.outlineThicknessM })
}

function authoredColliderPoints(modelRoot: THREE.Object3D, meshes: readonly THREE.Mesh[]) {
  const excluded = /(?:mirror|wheel|tyre|tire|rim|socket|attachment|empty|outline|cosmetic|accessory|spoiler|wing|splitter|skirt|canard)/i
  modelRoot.updateMatrixWorld(true)
  const result: THREE.Vector3[] = []
  const point = new THREE.Vector3()
  for (const mesh of meshes) {
    let ancestor: THREE.Object3D | null = mesh
    let omit = false
    while (ancestor && ancestor !== modelRoot) {
      if (excluded.test(ancestor.name)) { omit = true; break }
      ancestor = ancestor.parent
    }
    if (omit) continue
    const positions = mesh.geometry.getAttribute('position')
    if (!positions) continue
    mesh.updateWorldMatrix(true, false)
    for (let index = 0; index < positions.count; index += 1) {
      result.push(point.fromBufferAttribute(positions, index).applyMatrix4(mesh.matrixWorld).clone())
    }
  }
  return result
}

export class ArcadeVehicle {
  readonly group = new THREE.Group()
  readonly wheels: THREE.Mesh[] = []
  readonly record: VehicleRecord
  readonly physics: KunosLiteState
  readonly ride: OctagonalRideState
  readonly groundOffsetM = 0.015
  heading = 0
  roadName = 'UNNAMED ROAD'
  surface: 'asphalt' | 'grass' | 'air' = 'asphalt'
  modelLoaded = false
  collisionCount = 0
  private wheelVisuals: WheelVisual[] = []
  private wheelSpecs: OctagonalWheelSpec[]
  private hull: VehicleConvexHull
  private recoveryPose?: RecoveryPose
  private lastInput: DriveInput = { throttle: 0, brake: 0, steering: 0, handbrake: false }

  constructor(record: VehicleRecord = TOYOTA_GT86) {
    this.record = record
    this.physics = createKunosLiteState(record)
    this.ride = createOctagonalRideState(this.groundOffsetM, 4)
    this.hull = fallbackHull(record)
    this.wheelSpecs = wheelKeys.map((key, index) => {
      const [x, y, z] = record.geometry.wheels[key]
      const front = index < 2
      const suspension = front ? record.suspension.front : record.suspension.rear
      return {
        x, y, z,
        radius: record.geometry.wheelRadiusM,
        travelM: suspension.travelM,
        naturalFrequencyHz: suspension.naturalFrequencyHz,
        dampingRatio: suspension.dampingRatio,
        sprungMassKg: record.body.massKg * (front ? record.body.frontWeightFraction : 1 - record.body.frontWeightFraction) * 0.5,
        axle: front ? 'front' : 'rear',
      }
    })
    this.group.name = 'VEHICLE_ROOT'
    const gradient = toonGradient(record.presentation.toonBands)
    for (let index = 0; index < wheelKeys.length; index += 1) {
      this.buildProceduralWheel(record.geometry.wheels[wheelKeys[index]], index < 2, gradient)
    }
    this.updatePose()
  }

  get speed() { return this.physics.longitudinalSpeed }
  set speed(value: number) {
    const finite = Number.isFinite(value) ? value : 0
    this.physics.velocityX = Math.sin(this.heading) * finite
    this.physics.velocityZ = Math.cos(this.heading) * finite
    this.physics.longitudinalSpeed = finite
    this.physics.lateralSpeed = 0
  }

  get grounded() { return this.ride.groundedWheels > 0 }
  get collider() { return this.hull }

  async loadModel(modelUrl = '/cars/alpha/toyota-gt86/toyota-gt86-alpha.glb') {
    const gltf = await new GLTFLoader().loadAsync(modelUrl)
    const modelRoot = gltf.scene
    modelRoot.name = 'AUTHORED_VEHICLE_BODY'
    modelRoot.rotation.y = THREE.MathUtils.degToRad(this.record.asset.modelYawCorrectionDegrees)
    const gradient = toonGradient(this.record.presentation.toonBands)
    const meshes: THREE.Mesh[] = []
    modelRoot.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return
      meshes.push(object)
    })
    this.hull = buildVehicleConvexHull(authoredColliderPoints(modelRoot, meshes), {
      skinM: Math.max(0.012, this.record.presentation.outlineThicknessM),
    })
    for (const mesh of meshes) {
      const sources = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
      const materials = sources.map((source) => toonMaterial(source, gradient))
      mesh.material = Array.isArray(mesh.material) ? materials : materials[0]
      mesh.castShadow = false
      mesh.receiveShadow = true
      addOutline(mesh, this.record.presentation.outlineThicknessM)
    }
    this.group.add(modelRoot)
    this.modelLoaded = true
  }

  reset(x: number, z: number, heading: number, sampler: SurfaceSampler) {
    const surface = sampler(x, z)
    const y = surface.height + this.groundOffsetM
    this.heading = heading
    resetKunosLiteState(this.physics, this.record)
    resetOctagonalRideState(this.ride, y)
    this.group.position.set(x, y, z)
    this.roadName = surface.name || (surface.road ? 'UNNAMED ROAD' : 'OFF ROAD')
    this.surface = surface.road ? 'asphalt' : 'grass'
    this.recoveryPose = { x, y, z, heading }
    this.updatePose()
  }

  /** Returns to the most recent four-wheel grounded road pose. */
  recover(sampler: SurfaceSampler): boolean {
    const pose = this.recoveryPose
    if (!pose) return false
    const contact = sampler(pose.x, pose.z, pose.y)
    this.reset(pose.x, pose.z, pose.heading, sampler)
    this.group.position.y = Number.isFinite(contact.height) ? contact.height + this.groundOffsetM : pose.y
    this.ride.heightM = this.group.position.y
    this.updatePose()
    return true
  }

  update(delta: number, input: DriveInput, sampler: SurfaceSampler, resolveMotion?: MotionResolver) {
    const finiteState = [
      this.group.position.x, this.group.position.y, this.group.position.z, this.heading,
      this.physics.velocityX, this.physics.velocityZ, this.ride.heightM,
    ].every(Number.isFinite)
    if (!finiteState) { this.recover(sampler); return }

    const frameDelta = Math.min(Math.max(delta, 0), 0.05)
    this.lastInput = { ...input }
    const drive = clamp(input.throttle - input.brake, -1, 1)
    const steps = Math.max(1, Math.ceil(frameDelta / (1 / 120)))
    const stepDelta = frameDelta / steps
    for (let stepIndex = 0; stepIndex < steps; stepIndex += 1) {
      const contactsBefore = this.sampleWheelContacts(sampler, stepDelta)
      const movement = stepKunosLite(this.physics, this.record, {
        drive,
        steer: clamp(input.steering, -1, 1),
        handbrake: input.handbrake,
        wheels: this.ride.wheels.map((wheel, index) => ({
          contact: wheel.contact,
          loadN: wheel.loadN,
          surfaceGrip: contactsBefore[index]?.grip ?? 1,
        })),
      }, this.heading, stepDelta)

      this.heading += movement.headingDelta
      const startX = this.group.position.x
      const startZ = this.group.position.z
      const targetX = startX + movement.deltaX
      const targetZ = startZ + movement.deltaZ
      const bounds = this.worldHullBounds()
      const radius = Math.max(this.record.geometry.widthM * 0.48, this.record.geometry.wheelbaseM * 0.34)
      const resolved = resolveMotion?.(startX, startZ, targetX, targetZ, bounds.minY, bounds.maxY, radius)
        ?? { x: targetX, z: targetZ, collided: false }
      this.group.position.x = resolved.x
      this.group.position.z = resolved.z
      if (resolved.collided) {
        this.collisionCount += 1
        this.physics.velocityX *= 0.42
        this.physics.velocityZ *= 0.42
        this.physics.yawRate *= 0.58
      }

      const contactsAfter = this.sampleWheelContacts(sampler, stepDelta)
      stepOctagonalRide(this.ride, {
        massKg: this.record.body.massKg,
        lengthM: this.record.geometry.lengthM,
        widthM: this.record.geometry.widthM,
        heightM: this.record.geometry.heightM,
        wheelbaseM: this.record.geometry.wheelbaseM,
        frontWeightFraction: this.record.body.frontWeightFraction,
        antiRollFront: this.record.suspension.antiRollFront,
        antiRollRear: this.record.suspension.antiRollRear,
        centreOfMassHeightM: this.record.body.centreOfMassHeightM,
        longitudinalAccelerationMps2: this.physics.longitudinalAcceleration,
        lateralAccelerationMps2: this.physics.lateralAcceleration,
      }, this.wheelSpecs, contactsAfter.map((contact) => ({ height: contact.height })),
      this.physics.wheels.map((wheel) => wheel.rotationRadians), stepDelta)
      this.resolveChassisGroundContact(sampler, stepDelta)
      this.group.position.y = this.ride.heightM
    }

    const centre = sampler(this.group.position.x, this.group.position.z, this.group.position.y)
    this.roadName = centre.name || (centre.road ? 'UNNAMED ROAD' : 'OFF ROAD')
    this.surface = this.grounded ? (centre.road ? 'asphalt' : 'grass') : 'air'
    if (this.ride.heightM < centre.height - 120) { this.recover(sampler); return }
    if (centre.road && this.ride.groundedWheels === 4 && Math.abs(this.ride.verticalVelocityMps) < 1.5) {
      this.recoveryPose = {
        x: this.group.position.x,
        y: this.group.position.y,
        z: this.group.position.z,
        heading: this.heading,
      }
    }
    this.updateVisualWheels()
    this.updatePose()
  }

  createTelemetryFrame(sequence: number, simulationTimeSeconds: number): VehicleTelemetryFrameV1 {
    const wheels = this.physics.wheels.map((wheel, index) => ({
      index: wheel.index,
      position: telemetryWheelPositions[index],
      axle: wheel.axle,
      side: wheel.side,
      contact: wheel.contact,
      surfaceGripCoefficient: wheel.surfaceGrip,
      normalLoadNewtons: wheel.normalLoadN,
      steeringAngleRadians: wheel.steeringAngleRadians,
      angularVelocityRadiansPerSecond: wheel.angularVelocityRadPerSecond,
      longitudinalPatchSpeedMetersPerSecond: wheel.longitudinalPatchSpeedMps,
      lateralPatchSpeedMetersPerSecond: wheel.lateralPatchSpeedMps,
      slipRatio: wheel.slipRatio,
      slipAngleRadians: THREE.MathUtils.degToRad(wheel.slipAngleDegrees),
      frictionCoefficient: wheel.frictionCoefficient,
      combinedSlipNormalized: wheel.gripUsage,
      longitudinalForceNewtons: wheel.longitudinalForceN,
      lateralForceNewtons: wheel.lateralForceN,
      driveTorqueNewtonMeters: wheel.driveTorqueNm,
      brakeTorqueNewtonMeters: wheel.brakeTorqueNm,
      suspensionCompressionMeters: this.ride.wheels[index]?.compressionM ?? 0,
    }))
    return freezeVehicleTelemetryFrame({
      schema: 'daylight-club.vehicle-telemetry',
      schemaVersion: VEHICLE_TELEMETRY_SCHEMA_VERSION,
      sequence,
      simulationTimeSeconds,
      vehicle: { id: this.record.id, name: this.record.name, physicsModel: this.record.physicsModel },
      pose: {
        positionMeters: { x: this.group.position.x, y: this.group.position.y, z: this.group.position.z },
        headingRadians: this.heading,
        pitchRadians: this.ride.pitchRadians,
        rollRadians: this.ride.rollRadians,
      },
      motion: {
        velocityWorldMetersPerSecond: {
          x: this.physics.velocityX,
          y: this.ride.verticalVelocityMps,
          z: this.physics.velocityZ,
        },
        velocityBodyMetersPerSecond: {
          longitudinal: this.physics.longitudinalSpeed,
          lateral: this.physics.lateralSpeed,
          vertical: this.ride.verticalVelocityMps,
        },
        accelerationBodyMetersPerSecondSquared: {
          longitudinal: this.physics.longitudinalAcceleration,
          lateral: this.physics.lateralAcceleration,
        },
        yawRateRadiansPerSecond: this.physics.yawRate,
        bodySlipAngleRadians: Math.atan2(this.physics.lateralSpeed, Math.max(0.5, Math.abs(this.physics.longitudinalSpeed))),
      },
      inputs: {
        driveCommand: this.lastInput.throttle - this.lastInput.brake,
        steeringCommand: this.lastInput.steering,
        handbrake: this.lastInput.handbrake,
      },
      steering: {
        roadWheelAngleRadians: this.physics.steeringAngle,
        slipLimitedMaximumAngleRadians: THREE.MathUtils.degToRad(this.physics.steeringSlipLimitDegrees),
      },
      powertrain: { engineAngularVelocityRadiansPerSecond: this.physics.engineRpm * Math.PI / 30, gear: this.physics.gear },
      chassis: {
        groundedWheelCount: this.ride.groundedWheels,
        supportGrade: 'alpha-world-surface',
        surface: this.surface,
        longitudinalForceNewtons: this.physics.longitudinalForceN,
        lateralForceNewtons: this.physics.lateralForceN,
      },
      tyreLimits: {
        peakSlipRatio: this.record.tyres.peakSlipRatio,
        peakSlipAngleRadians: THREE.MathUtils.degToRad(this.record.tyres.peakSlipAngleDegrees),
      },
      wheels,
    })
  }

  private buildProceduralWheel(position: [number, number, number], front: boolean, gradient: THREE.Texture) {
    const steering = new THREE.Group()
    steering.position.fromArray(position)
    const rolling = new THREE.Group()
    steering.add(rolling)
    const tyreGeometry = new THREE.CylinderGeometry(
      this.record.geometry.wheelRadiusM,
      this.record.geometry.wheelRadiusM,
      this.record.geometry.wheelWidthM,
      8,
    )
    tyreGeometry.rotateZ(Math.PI / 2)
    const tyre = new THREE.Mesh(tyreGeometry, new THREE.MeshToonMaterial({ color: '#202625', gradientMap: gradient }))
    tyre.name = 'PROCEDURAL_OCTAGONAL_TYRE'
    tyre.receiveShadow = true
    addOutline(tyre, 0.012)
    rolling.add(tyre)
    const rimGeometry = new THREE.CylinderGeometry(
      this.record.geometry.wheelRadiusM * 0.61,
      this.record.geometry.wheelRadiusM * 0.61,
      this.record.geometry.wheelWidthM + 0.008,
      8,
    )
    rimGeometry.rotateZ(Math.PI / 2)
    const rim = new THREE.Mesh(rimGeometry, new THREE.MeshToonMaterial({ color: '#b5b6ae', gradientMap: gradient }))
    rim.name = 'PROCEDURAL_RIM_PLACEHOLDER'
    addOutline(rim, 0.007)
    rolling.add(rim)
    this.group.add(steering)
    this.wheels.push(tyre)
    this.wheelVisuals.push({ steering, rolling, front, baseY: position[1] })
  }

  private sampleWheelContacts(sampler: SurfaceSampler, delta: number) {
    const sin = Math.sin(this.heading)
    const cos = Math.cos(this.heading)
    return this.wheelSpecs.map((spec) => {
      const worldX = this.group.position.x - cos * spec.x - sin * spec.z
      const worldZ = this.group.position.z + sin * spec.x - cos * spec.z
      const referenceHeight = this.ride.heightM + spec.y - spec.radius
      const surface = sampler(worldX, worldZ, referenceHeight)
      const maximumPenetration = spec.travelM * 0.58 + 0.08
        + Math.max(0, -this.ride.verticalVelocityMps) * delta
      return {
        ...surface,
        height: surface.height - referenceHeight > maximumPenetration
          ? Number.NEGATIVE_INFINITY
          : surface.height,
      }
    })
  }

  private resolveChassisGroundContact(sampler: SurfaceSampler, delta: number) {
    if (this.ride.groundedWheels === 4
      && this.ride.verticalVelocityMps >= -0.5
      && Math.abs(this.ride.pitchRadians) <= 0.06
      && Math.abs(this.ride.rollRadians) <= 0.06) return
    const pitchInertia = this.record.body.massKg * (this.record.geometry.lengthM ** 2 + this.record.geometry.heightM ** 2) / 12
    const rollInertia = this.record.body.massKg * (this.record.geometry.widthM ** 2 + this.record.geometry.heightM ** 2) / 12
    const inverseMass = 1 / this.record.body.massKg
    const inversePitch = 1 / pitchInertia
    const inverseRoll = 1 / rollInertia
    const angularSweep = (
      Math.abs(this.ride.pitchVelocityRadiansPerSecond) * this.record.geometry.lengthM * 0.5
      + Math.abs(this.ride.rollVelocityRadiansPerSecond) * this.record.geometry.widthM * 0.5
    ) * delta
    const maximumPenetration = Math.min(0.75, 0.08 + Math.max(0, -this.ride.verticalVelocityMps) * delta + angularSweep)
    for (let pass = 0; pass < 4; pass += 1) {
      this.updatePose()
      let deepest: { local: THREE.Vector3; penetration: number } | undefined
      for (const probe of lowestVehicleHullVertices(this.hull, this.group.quaternion, 20, 0.62)) {
        const pointY = this.ride.heightM + probe.rotated.y
        const contact = sampler(this.group.position.x + probe.rotated.x, this.group.position.z + probe.rotated.z, pointY)
        const penetration = contact.height - pointY
        if (penetration <= 0.0015 || penetration > maximumPenetration) continue
        if (!deepest || penetration > deepest.penetration) deepest = { local: probe.local, penetration }
      }
      if (!deepest) break
      const pitchJacobian = -deepest.local.z
      const rollJacobian = deepest.local.x
      const effectiveInverseMass = inverseMass + pitchJacobian ** 2 * inversePitch + rollJacobian ** 2 * inverseRoll
      const positionImpulse = deepest.penetration * 0.88 / effectiveInverseMass
      this.ride.heightM += positionImpulse * inverseMass
      this.ride.pitchRadians += positionImpulse * pitchJacobian * inversePitch
      this.ride.rollRadians += positionImpulse * rollJacobian * inverseRoll
      const contactVelocity = this.ride.verticalVelocityMps
        + this.ride.pitchVelocityRadiansPerSecond * pitchJacobian
        + this.ride.rollVelocityRadiansPerSecond * rollJacobian
      if (contactVelocity < 0) {
        const impulse = -contactVelocity * 1.04 / effectiveInverseMass
        this.ride.verticalVelocityMps += impulse * inverseMass
        this.ride.pitchVelocityRadiansPerSecond += impulse * pitchJacobian * inversePitch
        this.ride.rollVelocityRadiansPerSecond += impulse * rollJacobian * inverseRoll
      }
    }
  }

  private worldHullBounds() {
    let minY = Number.POSITIVE_INFINITY
    let maxY = Number.NEGATIVE_INFINITY
    for (const vertex of this.hull.vertices) {
      const y = vertex.clone().applyQuaternion(this.group.quaternion).y + this.ride.heightM
      minY = Math.min(minY, y)
      maxY = Math.max(maxY, y)
    }
    return { minY, maxY }
  }

  private updateVisualWheels() {
    for (let index = 0; index < this.wheelVisuals.length; index += 1) {
      const visual = this.wheelVisuals[index]
      visual.rolling.rotation.x = -(this.physics.wheels[index]?.rotationRadians ?? 0)
      visual.steering.rotation.y = visual.front ? this.physics.wheels[index]?.steeringAngleRadians ?? 0 : 0
      visual.steering.position.y = visual.baseY + (this.ride.wheels[index]?.compressionM ?? 0)
    }
  }

  private updatePose() {
    this.group.position.y = this.ride.heightM
    const yaw = new THREE.Quaternion().setFromAxisAngle(worldUp, this.heading + Math.PI)
    const pitch = new THREE.Quaternion().setFromAxisAngle(localX, this.ride.pitchRadians)
    const roll = new THREE.Quaternion().setFromAxisAngle(localZ, this.ride.rollRadians)
    this.group.quaternion.copy(yaw).multiply(pitch).multiply(roll)
  }
}
